/**
 * Single source of truth for deciding a subscription's display modality.
 *
 * Never re-derive modality inline elsewhere — call `getSubscriptionModality`.
 *
 * Modalities:
 * - 'one_time'   — single charge (talleres). `sub.type === 'one_time'`
 *                  (equivalently `plan.type === 'charge'`). Perpetual access,
 *                  no expiry, no renewal.
 * - 'membership' — recurring with INFINITE billing cycles
 *                  (`plan.billing_cycles == null`). MUST render byte-identically
 *                  to today — this is the hard invariant.
 * - 'finite'     — recurring with a finite number of cycles
 *                  (`plan.billing_cycles` is a number > 0). Includes the
 *                  completed case where `remaining_billing_cycles` is 0 / "0"
 *                  (finite COMPLETED, status 'paid').
 */
export type SubscriptionModality = 'one_time' | 'finite' | 'membership';

/**
 * Minimal structural input so the helper is trivially unit-testable. A full
 * `Subscription` satisfies this shape, so it can be passed without casting.
 */
export interface SubscriptionModalityInput {
  type: string;
  plan?: { type?: string | null; billing_cycles?: number | null } | null;
  remaining_billing_cycles?: number | string | null;
}

export const getSubscriptionModality = (
  sub: SubscriptionModalityInput
): SubscriptionModality => {
  // One-time wins on either the subscription discriminant or the plan type.
  if (sub.type === 'one_time' || sub.plan?.type === 'charge') {
    return 'one_time';
  }

  // Recurring: distinguish infinite membership from finite by plan.billing_cycles.
  // `== null` matches both null and undefined (missing plan / missing field) and
  // preserves today's behaviour for memberships.
  const billingCycles = sub.plan?.billing_cycles;
  if (billingCycles != null && billingCycles > 0) {
    return 'finite';
  }

  return 'membership';
};

/**
 * Shared one_time access gate — the SINGLE source of truth for one-time access,
 * used by BOTH access checks (content access in `isSubscribed` and checkout
 * routing in the Subscription provider via `isSubscribedForCheckout`). Living
 * here guarantees the two gates can never DRIFT on one_time semantics.
 *
 * ADDITIVE / no-op for recurring: returns `true` for membership AND finite, so
 * each caller's pre-existing (and intentionally different) recurring state-list
 * result is preserved byte-identically. A one_time sub grants access ONLY in a
 * terminal/paid state ('paid'/'completed'); one_time 'waiting_for_gateway' /
 * 'not_paid' / 'retrying' = pago en proceso → NO access.
 */
export const oneTimeGrantsAccess = (
  sub: SubscriptionModalityInput & { status?: string | null }
): boolean => {
  if (getSubscriptionModality(sub) !== 'one_time') return true;
  return sub.status === 'paid' || sub.status === 'completed';
};
